import moment from 'moment';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';

// THIN-8141 copy and state for the progress-note due indicators. The visit and day numbers come from
// the payer's docs-due settings, so copy never hardcodes 10 or 14.

export const ordinal = (n: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

/** Where dueDate sits relative to `date` (YYYY-MM-DD). */
export const progressNoteDueStatus = (
  due: ProgressNoteDue | null | undefined,
  date: string
): 'OVERDUE' | 'DUE_TODAY' | 'UPCOMING' | null => {
  if (!due?.dueDate || !date) return null;
  if (due.dueDate < date) return 'OVERDUE';
  if (due.dueDate === date) return 'DUE_TODAY';
  return 'UPCOMING';
};

const ruleLabel = (due: ProgressNoteDue): string => {
  if (due.rule === 'VISIT') return due.visitMax ? `${ordinal(due.visitMax)} visit` : 'visit rule';
  return due.dayRuleDay ? `${ordinal(due.dayRuleDay)} day` : 'day rule';
};

const shortDate = (date: string) => moment(date, 'YYYY-MM-DD').format('ddd M/D');

/** Scheduler card and assigned-pill tooltip; only meaningful when the note is due or overdue. */
export const schedulerProgressNoteDueTitle = (due: ProgressNoteDue, date: string): string => {
  if (progressNoteDueStatus(due, date) === 'OVERDUE') {
    // THIN-8192: the card can show two, so the date tells them apart
    return `${due.discipline} Progress Note overdue since ${shortDate(due.dueDate)}`;
  }
  return `${due.discipline} Progress Note due today. This is the ${ruleLabel(due)}.`;
};

/** Planner cell tooltip: the date, which rule set it, and whether it can still move. */
export const plannerProgressNoteDueTitle = (due: ProgressNoteDue, today: string): string[] => {
  const heading = `${due.discipline} Progress Note`;
  if (progressNoteDueStatus(due, today) === 'OVERDUE') {
    return [`${heading} overdue since ${shortDate(due.dueDate)}`];
  }
  if (due.projected) {
    const planned =
      due.visitMax !== null && due.visitsCompleted !== null
        ? due.visitMax - due.visitsCompleted
        : null;
    const lines = [
      `${heading} projected for ${shortDate(due.dueDate)}`,
      planned !== null
        ? `The ${ruleLabel(due)} is projected to land here, counting ${
            due.visitsCompleted
          } delivered plus ${planned} planned. This date moves if the plan changes.`
        : `The ${ruleLabel(due)} is projected to land here. This date moves if the plan changes.`,
    ];
    if (due.dayRuleDate && due.dayRuleDay) {
      lines.push(`Day rule: ${ordinal(due.dayRuleDay)} day, ${shortDate(due.dayRuleDate)}.`);
    }
    return lines;
  }
  return [
    `${heading} due ${shortDate(due.dueDate)}`,
    due.rule === 'VISIT'
      ? `The ${ruleLabel(due)} has been delivered.`
      : `The ${ruleLabel(due)} decides.`,
    'This date is settled. It cannot move later.',
  ];
};
